import { Mapper } from 'modules/cm/mapper/types';
import { API_FIELDS, PepperReviewSummary } from 'modules/cm/api/cases/PepperReview/types';
import {
  BorrowerSection,
  FIELDS,
  GeneralSection,
  LoanSection,
  MortgagePropertySection,
  PepperReview,
  SUBDOMAINS,
} from 'modules/cm/domain/PepperUploadPreview/PepperUploadPreview';
import { CaseDetails } from 'modules/cm/pages/CaseDetailsPage/CaseDetailsPage.types';
import { getResource } from 'modules/cm/utils/resources';
import { ArgumentsOf } from 'utils/types';

type PepperReviewSummaryApplicant = NonNullable<PepperReviewSummary[API_FIELDS.APPLICANT_1 | API_FIELDS.APPLICANT_2]>;
type Resources = CaseDetails['resources'];

const getResourceValue = (...args: ArgumentsOf<typeof getResource>) => {
  const resource = getResource(...args);
  return resource ? resource.option : null;
};

const mapGeneral = (api: PepperReviewSummary, resources: Resources): GeneralSection => {
  return {
    [FIELDS.CASE_ID]: api[API_FIELDS.CASE_ID],
    [FIELDS.ORIGINATION_ID]: api[API_FIELDS.ORIGINATION_ID],
    [FIELDS.NUMBER_OF_APPLICANTS]: api[API_FIELDS.NUMBER_OF_APPLICANTS],
    [FIELDS.ACCOUNT_NUMBER]: api[API_FIELDS.ACCOUNT_NUMBER],
    [FIELDS.SORT_CODE]: api[API_FIELDS.SORT_CODE],
  };
};

const mapMortgageProperty = (api: PepperReviewSummary, resources: Resources): MortgagePropertySection => {
  return {
    [FIELDS.TITLE_NUMBER]: api[API_FIELDS.TITLE_NUMBER],
    [FIELDS.FLAT_NUMBER]: api[API_FIELDS.FLAT_NUMBER],
    [FIELDS.BUILDING_NAME]: api[API_FIELDS.BUILDING_NAME],
    [FIELDS.BUILDING_NUMBER]: api[API_FIELDS.BUILDING_NUMBER],
    [FIELDS.STREET]: api[API_FIELDS.STREET],
    [FIELDS.CITY]: api[API_FIELDS.CITY],
    [FIELDS.PROPERTY_POST_CODE]: api[API_FIELDS.PROPERTY_POST_CODE],
    [FIELDS.PROPERTY_TYPE]: getResourceValue(resources.propertyTypes, api[API_FIELDS.PROPERTY_TYPE]),
    [FIELDS.TENURE]: getResourceValue(resources.tenureTypes, api[API_FIELDS.TENURE]),
    [FIELDS.PROPERTY_BUILT_YEAR]: api[API_FIELDS.PROPERTY_BUILT_YEAR],
    [FIELDS.ACTUAL_PROPERTY_VALUE]: api[API_FIELDS.ACTUAL_PROPERTY_VALUE],
    [FIELDS.INSPECTION_DATE]: api[API_FIELDS.INSPECTION_DATE],
  };
};

const mapLoan = (api: PepperReviewSummary, resources: Resources): LoanSection => {
  return {
    [FIELDS.PEPPER_PURPOSE_OF_THE_LOAN]: getResourceValue(
      resources.pepperPurposesOfTheLoan,
      api[API_FIELDS.PEPPER_PURPOSE_OF_THE_LOAN],
    ),
    [FIELDS.FCA_LOAN_PURPOSE]: getResourceValue(resources.fcaLoanPurposes, api[API_FIELDS.FCA_LOAN_PURPOSE]),
    [FIELDS.NET_LOAN]: api[API_FIELDS.NET_LOAN],
    [FIELDS.IS_BUY_TO_LET]: api[API_FIELDS.IS_BUY_TO_LET],
    [FIELDS.IS_MARGIN_ACTIVE]: api[API_FIELDS.IS_MARGIN_ACTIVE],
    [FIELDS.IS_APP_ACTIVE]: api[API_FIELDS.IS_APP_ACTIVE],
    [FIELDS.PRODUCT_FEE]: api[API_FIELDS.PRODUCT_FEE],
    [FIELDS.PRODUCT_FEE_PAYMENT]: api[API_FIELDS.PRODUCT_FEE_PAYMENT],
    [FIELDS.COMPLETION_DATE]: api[API_FIELDS.COMPLETION_DATE],
    [FIELDS.VARIABLE_TERM_START_DATE]: api[API_FIELDS.VARIABLE_TERM_START_DATE],
    [FIELDS.NUMBER_OF_PAYMENTS]: api[API_FIELDS.NUMBER_OF_PAYMENTS],
    [FIELDS.FIXED_INTEREST_RATE]: api[API_FIELDS.FIXED_INTEREST_RATE],
    [FIELDS.REVERT_MARGIN]: api[API_FIELDS.REVERT_MARGIN],
    [FIELDS.LVR]: api[API_FIELDS.LVR],
  };
};

const mapBorrower = (api: PepperReviewSummary, resources: Resources): BorrowerSection => {
  const mapApplicant = (
    api: PepperReviewSummaryApplicant,
  ): NonNullable<BorrowerSection[FIELDS.APPLICANT_1 | FIELDS.APPLICANT_2]> => {
    return {
      [FIELDS.BORROWER_TITLE]: getResourceValue(resources.title, api[API_FIELDS.BORROWER_TITLE]),
      [FIELDS.BORROWER_LAST_NAME]: api[API_FIELDS.BORROWER_LAST_NAME],
      [FIELDS.BORROWER_FIRST_NAME]: api[API_FIELDS.BORROWER_FIRST_NAME],
      [FIELDS.BORROWER_GENDER]: getResourceValue(resources.gender, api[API_FIELDS.BORROWER_GENDER]),
      [FIELDS.BORROWER_DATE_OF_BIRTH]: api[API_FIELDS.BORROWER_DATE_OF_BIRTH],
      [FIELDS.BORROWER_EMPLOYMENT_STATUS]: getResourceValue(
        resources.employmentStatuses,
        api[API_FIELDS.BORROWER_EMPLOYMENT_STATUS],
      ),
      [FIELDS.BORROWER_TOTAL_CURRENT_INCOME]: api[API_FIELDS.BORROWER_TOTAL_CURRENT_INCOME],
      [FIELDS.BORROWER_PHONE_HOME]: api[API_FIELDS.BORROWER_PHONE_HOME],
      [FIELDS.BORROWER_PHONE_MOBILE]: api[API_FIELDS.BORROWER_PHONE_MOBILE],
      [FIELDS.BORROWER_PHONE_WORK]: api[API_FIELDS.BORROWER_PHONE_WORK],
      [FIELDS.BORROWER_EMAIL]: api[API_FIELDS.BORROWER_EMAIL],
      [FIELDS.BORROWER_ADDITIONAL_EMAIL]: api[API_FIELDS.BORROWER_ADDITIONAL_EMAIL],
    };
  };
  return {
    [FIELDS.APPLICANT_1]: mapApplicant(api[API_FIELDS.APPLICANT_1]),
    [FIELDS.APPLICANT_2]: api[API_FIELDS.APPLICANT_2]
      ? mapApplicant(api[API_FIELDS.APPLICANT_2] as NonNullable<typeof api[API_FIELDS.APPLICANT_2]>)
      : null,
  };
};

type PepperReviewDataMapper = Omit<
  Mapper<PepperReviewSummary, PepperReview>,
  'mapApiToDomain' | 'mapDomainFieldToAPI' | 'mapDomainNameToAPI'
> & {
  mapApiToDomain: (api: PepperReviewSummary, resources: Resources) => PepperReview;
};

const mapper: PepperReviewDataMapper = {
  mapApiToDomain: (api: PepperReviewSummary, resources: Resources): PepperReview => {
    return {
      [SUBDOMAINS.GENERAL]: mapGeneral(api, resources),
      [SUBDOMAINS.MORTGAGE_PROPERTY]: mapMortgageProperty(api, resources),
      [SUBDOMAINS.LOAN]: mapLoan(api, resources),
      [SUBDOMAINS.BORROWER]: mapBorrower(api, resources),
    };
  },
};

export default mapper;
